import React, { useMemo, useState, useEffect } from "react";
import { Table, Card, CardBody, CardTitle, Badge, Button, Row, Col, Input, InputGroup, InputGroupText, Label } from "reactstrap";
import formatCurrency from "constants/formatCurrency";

const INCLUDED_OPTIONS = [
    "corepark", "prospr", "amano", "spothero",
    "surepark", "resolve", "monthly_parking", "events",
];

const PAGE_SIZE = 10;

const LocationsPerformanceTable = ({
    reports,
    selectedManager,
    selectedStructure,
    included,
    filterBySource
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showDetails, setShowDetails] = useState(false); // NEW STATE

    const hasSourceData = (data, source) => {
        switch (source) {
            case "corepark": {
                const cp = data.corepark || {};
                return ((cp.cash || 0) + (cp.square?.square_total || 0) + (cp.tickets || 0) + (cp.fee || 0) + (cp.tax || 0)) > 0;
            }
            case "prospr": return (data.prospr?.hours || 0) > 0;
            case "amano": return (data.amano?.fee || 0) + (data.amano?.transactions || 0) > 0;
            case "spothero": return (data.spothero?.amount || 0) > 0;
            case "surepark": return (data.surepark?.amount || 0) > 0;
            case "resolve": return (data.resolve?.amount || 0) > 0;
            case "monthly_parking": return (data.monthly_parking_orders?.amount || 0) + (data.monthly_parking_orders?.orders || 0) > 0;
            case "events": return (data.events?.income || 0) + (data.events?.events || 0) > 0;
            default: return false;
        }
    };

    console.log("reports:", reports)

    const tableData = useMemo(() => {
        return Object.entries(reports)
            .filter(([loc, platforms]) => {
                // 1. Manager Filter
                if (selectedManager !== "All Managers" && (platforms.account_manager || "").toLowerCase() !== selectedManager.toLowerCase()) return false;

                // 2. Structure Filter
                if (selectedStructure !== "All Types" && (platforms.operating_structure || "").toLowerCase() !== selectedStructure.toLowerCase()) return false;

                // 3. Search Filter
                if (searchQuery && !loc.toLowerCase().includes(searchQuery.toLowerCase())) return false;

                // 4. Source Filter
                if (filterBySource) {
                    const activeSources = included.includes("all") ? INCLUDED_OPTIONS : included;
                    const hasAny = activeSources.some(src => hasSourceData(platforms, src));
                    if (!hasAny) return false;
                }
                return true;
            })
            .map(([locationName, data]) => {
                const isLease = (data.operating_structure || "").toLowerCase() === "lease";

                // --- REVENUE BREAKDOWN ---
                const fees = (data.fees?.amount || 0);
                // Corepark splits (only if not lease)
                const cpCash = isLease ? 0 : (data.corepark?.cash || 0);
                const sqCard = isLease ? 0 : (data.corepark?.square?.square_total || 0);

                const leaseSettlement = (data.lease_payments?.payments || [])
                    .reduce((sum, p) => sum + (p.amount || 0), 0);

                const spothero = (data.spothero?.amount || 0);
                const surepark = (data.surepark?.amount || 0);
                const resolve = (data.resolve?.amount || 0);
                const amano = (data.amano?.fee || 0); // Note: You had amano.fee in your previous totalDataSources logic
                const events = (data.events?.income || 0);
                const monthlies = (data.monthly_parking_orders?.amount || 0);

                const totalRevenue = fees + cpCash + sqCard + leaseSettlement + spothero + surepark + resolve + amano + events + monthlies;

                // --- EXPENSE BREAKDOWN ---
                const totalLabor = (data.prospr?.cost || 0);

                // Costs = Total Costs object (includes Lot Rent + Operating Costs)
                // In your logic: totalExpense = totalPayables + lotExpenses + totalLabor
                // Since totalPayables + lotExpenses == data.costs.amount, we use that for "Costs" column
                const totalCosts = (data.costs?.amount || 0);

                const totalExpense = totalCosts + totalLabor;
                const netProfit = totalRevenue - totalExpense;

                return {
                    locationName,
                    isLease,
                    // Breakdowns
                    fees, cpCash, sqCard, leaseSettlement, spothero, surepark, resolve, amano, events, monthlies,
                    totalCosts, totalLabor,
                    // Totals
                    totalRevenue,
                    totalExpense,
                    netProfit
                };
            })
            .sort((a, b) => b.netProfit - a.netProfit);

    }, [reports, selectedManager, selectedStructure, included, filterBySource, searchQuery]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedManager, selectedStructure, included, filterBySource, searchQuery]);

    // CSV Export Function
    // CSV Export Function
    const handleExportCsv = () => {
        if (!tableData || tableData.length === 0) return;

        let headers = ["Location Name", "Type"];

        if (showDetails) {
            headers.push("Fees", "CP Cash", "SQ Card", "Lease Settlement", "Spothero", "Surepark", "Resolve", "Amano", "Events", "Monthlies");
        }
        headers.push("Total Revenue");

        if (showDetails) {
            headers.push("Costs", "Labor");
        }
        headers.push("Total Expense", "Net Profit");

        const csvRows = tableData.map(row => {
            const escape = (text) => `"${String(text || "").replace(/"/g, '""')}"`;

            let cols = [
                escape(row.locationName),
                escape(row.isLease ? "LEASE" : "NON-LEASE")
            ];

            if (showDetails) {
                cols.push(
                    row.fees, row.cpCash, row.sqCard, row.leaseSettlement,
                    row.spothero, row.surepark, row.resolve, row.amano,
                    row.events, row.monthlies
                );
            }
            cols.push(row.totalRevenue);

            if (showDetails) {
                cols.push(row.totalCosts, row.totalLabor);
            }
            cols.push(row.totalExpense, row.netProfit);

            return cols.join(",");
        });

        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Locations_Performance_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Pagination Calculations
    const totalItems = tableData.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const currentTableData = tableData.slice(startIndex, startIndex + PAGE_SIZE);

    const getColor = (val) => {
        if (val > 0) return "#22C55E"; // Green
        if (val < 0) return "#f87171"; // Red
        return "#777"; // Dimmed Grey for 0
    };

    const getColorExpense = (val) => {
        if (val > 0) return "#f87171"; // Red
        return "#777"; // Dimmed Grey for 0
    };

    if (Object.keys(reports).length === 0) {
        return null;
    }

    return (
        <Card style={{ backgroundColor: "#2f2f2f", borderRadius: 14, border: "1px solid rgba(255,255,255,0.1)" }}>
            <CardBody>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
                    <CardTitle className="h4 mb-0" style={{ color: "#fff", fontWeight: 700 }}>
                        Locations by Net Profit
                    </CardTitle>

                    {/* Checkbox Centered */}
                    <div className="d-flex align-items-center">
                        <Input
                            type="checkbox"
                            id="showDetailsCheckbox"
                            checked={showDetails}
                            onChange={(e) => setShowDetails(e.target.checked)}
                            style={{ cursor: "pointer" }}
                        />
                        <Label check for="showDetailsCheckbox" className="mb-0 ms-2 text-white" style={{ cursor: "pointer", userSelect: "none" }}>
                            Show Data Source Columns
                        </Label>
                    </div>

                    <div className="d-flex gap-2 align-items-center" style={{ width: "100%", maxWidth: "500px" }}>
                        {/* Search Input */}
                        <InputGroup size="sm" style={{ flex: 1 }}>
                            <InputGroupText style={{ backgroundColor: "#333", border: "1px solid rgba(255,255,255,0.1)", color: "#ccc" }}>
                                <i className="bx bx-search"></i>
                            </InputGroupText>
                            <Input
                                placeholder="Search location..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ backgroundColor: "#333", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                            />
                        </InputGroup>

                        {/* Export Button */}
                        <Button
                            color="success"
                            size="sm"
                            onClick={handleExportCsv}
                            style={{ whiteSpace: "nowrap", fontWeight: 600 }}
                        >
                            <i className="bx bx-download me-1"></i> Export CSV
                        </Button>
                    </div>

                    <div style={{ color: "#ccc", fontSize: 13, whiteSpace: "nowrap" }}>
                        Showing {startIndex + 1} - {Math.min(startIndex + PAGE_SIZE, totalItems)} of {totalItems}
                    </div>
                </div>

                <div className="table-responsive">
                    <Table className="table-dark table-hover align-middle mb-0" style={{ fontSize: 14 }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ borderTopLeftRadius: 8, padding: "12px 16px" }}>Location Name</th>
                                <th style={{ padding: "12px 16px" }}>Type</th>
                                {showDetails && (
                                    <>
                                        <th className="text-end text-muted">Fees</th>
                                        <th className="text-end text-muted">CP Cash</th>
                                        <th className="text-end text-muted">SQ Card</th>
                                        <th className="text-end text-muted">Lease</th>
                                        <th className="text-end text-muted">Spothero</th>
                                        <th className="text-end text-muted">Surepark</th>
                                        <th className="text-end text-muted">Resolve</th>
                                        <th className="text-end text-muted">Amano</th>
                                        <th className="text-end text-muted">Events</th>
                                        <th className="text-end text-muted">Monthlies</th>
                                    </>
                                )}
                                <th className="text-end" style={{ padding: "12px 16px" }}>Total Revenue</th>
                                {showDetails && (
                                    <>
                                        <th className="text-end text-muted">Costs</th>
                                        <th className="text-end text-muted">Labor</th>
                                    </>
                                )}
                                <th className="text-end" style={{ padding: "12px 16px" }}>Total Expense</th>
                                <th className="text-end" style={{ borderTopRightRadius: 8, padding: "12px 16px" }}>Net Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentTableData.map((row, index) => (
                                <tr key={index} style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                    <td style={{ fontWeight: 600, padding: "12px 16px" }}>
                                        {row.locationName}
                                    </td>
                                    <td style={{ padding: "12px 16px" }}>
                                        {row.isLease ? (
                                            <Badge color="warning" className="text-dark">LEASE</Badge>
                                        ) : (
                                            <Badge color="secondary">NON-LEASE</Badge>
                                        )}
                                    </td>
                                    {showDetails && (
                                        <>
                                            <td className="text-end" style={{ color: getColor(row.fees) }}>{formatCurrency(row.fees)}</td>
                                            <td className="text-end" style={{ color: getColor(row.cpCash) }}>{formatCurrency(row.cpCash)}</td>
                                            <td className="text-end" style={{ color: getColor(row.sqCard) }}>{formatCurrency(row.sqCard)}</td>
                                            <td className="text-end" style={{ color: getColor(row.leaseSettlement) }}>{formatCurrency(row.leaseSettlement)}</td>
                                            <td className="text-end" style={{ color: getColor(row.spothero) }}>{formatCurrency(row.spothero)}</td>
                                            <td className="text-end" style={{ color: getColor(row.surepark) }}>{formatCurrency(row.surepark)}</td>
                                            <td className="text-end" style={{ color: getColor(row.resolve) }}>{formatCurrency(row.resolve)}</td>
                                            <td className="text-end" style={{ color: getColor(row.amano) }}>{formatCurrency(row.amano)}</td>
                                            <td className="text-end" style={{ color: getColor(row.events) }}>{formatCurrency(row.events)}</td>
                                            <td className="text-end" style={{ color: getColor(row.monthlies) }}>{formatCurrency(row.monthlies)}</td>
                                        </>
                                    )}
                                    <td className="text-end" style={{ color: getColor(row.totalRevenue), fontWeight: 500, padding: "12px 16px" }}>
                                        ${formatCurrency(row.totalRevenue)}
                                    </td>
                                    {showDetails && (
                                        <>
                                            <td className="text-end" style={{ color: getColorExpense(row.totalCosts) }}>{formatCurrency(row.totalCosts)}</td>
                                            <td className="text-end" style={{ color: getColorExpense(row.totalLabor) }}>{formatCurrency(row.totalLabor)}</td>
                                        </>
                                    )}
                                    <td className="text-end" style={{ color: "#f87171", fontWeight: 500, padding: "12px 16px" }}>
                                        ${formatCurrency(row.totalExpense)}
                                    </td>
                                    <td className="text-end" style={{ padding: "12px 16px" }}>
                                        <span
                                            style={{
                                                color: row.netProfit >= 0 ? "#22C55E" : "#f87171",
                                                fontWeight: 800,
                                                background: row.netProfit >= 0 ? "rgba(34, 197, 94, 0.1)" : "rgba(248, 113, 113, 0.1)",
                                                padding: "4px 8px",
                                                borderRadius: 6
                                            }}
                                        >
                                            ${formatCurrency(row.netProfit)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination Controls */}
                {totalItems > PAGE_SIZE && (
                    <Row className="align-items-center mt-3">
                        <Col className="text-end">
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ marginRight: 8 }}
                            >
                                Previous
                            </Button>
                            <span style={{ color: "#fff", margin: "0 10px", fontSize: 14 }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ marginLeft: 8 }}
                            >
                                Next
                            </Button>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default LocationsPerformanceTable;